import { SMART_GUIDE_SNAP_THRESHOLD } from './constants';
import { ActiveSmartGuide, SnapResult } from './types';

export interface SnapTargetRect {
  left: number;
  top: number;
  width: number;
  height: number;
}

interface CalculateMoveSnapParams {
  itemLeft: number;
  itemTop: number;
  itemWidth: number;
  itemHeight: number;
  canvasWidth: number;
  canvasHeight: number;
  /** 스냅 대상이 되는 다른 아이템들의 visible rect (드래그 대상 제외) */
  targets: SnapTargetRect[];
}

interface SnapCandidate {
  /** 스냅 확정 시의 새 left(또는 top) */
  snapped: number;
  /** 현재 위치와 스냅 지점 사이 거리 — 축별로 최소인 후보가 이긴다 */
  dist: number;
  guide: ActiveSmartGuide;
}

const pickNearest = (
  candidates: SnapCandidate[],
): SnapCandidate | undefined => {
  let best: SnapCandidate | undefined;
  for (const c of candidates) {
    if (c.dist > SMART_GUIDE_SNAP_THRESHOLD) continue;
    // 같은 거리면 먼저 넣은 후보(캔버스) 우선.
    if (best === undefined || c.dist < best.dist) best = c;
  }
  return best;
};

/**
 * 드래그 이동 스냅 — 캔버스(가장자리·중앙)와 다른 아이템(가장자리·중앙)을 한 번에 계산해
 * 축별로 가장 가까운 스냅 지점을 채택한다.
 *
 * - 캔버스 후보: 내 left↔0, 내 right↔canvasWidth, 내 center↔canvas center.
 * - 아이템 후보: target 의 left/center/right × 내 left/center/right 9조합 (모서리끼리,
 *   모서리-중심 정렬 전부).
 *
 * 캔버스 후보끼리도 최단거리로 경쟁한다 — 구 calculate-canvas-snap 의
 * left > right > center 우선순위 체인과 달리, 여러 후보가 동시에 threshold 안이면
 * 가장 가까운 쪽에 스냅된다(Figma 방식, 의도된 변경).
 */
export function calculateMoveSnap({
  itemLeft,
  itemTop,
  itemWidth,
  itemHeight,
  canvasWidth,
  canvasHeight,
  targets,
}: CalculateMoveSnapParams): SnapResult {
  // 내 anchor: [값, snapped 계산용 offset]
  const anchorsX: Array<[number, number]> = [
    [itemLeft, 0],
    [itemLeft + itemWidth / 2, itemWidth / 2],
    [itemLeft + itemWidth, itemWidth],
  ];
  const anchorsY: Array<[number, number]> = [
    [itemTop, 0],
    [itemTop + itemHeight / 2, itemHeight / 2],
    [itemTop + itemHeight, itemHeight],
  ];

  const xCandidates: SnapCandidate[] = [
    {
      snapped: 0,
      dist: Math.abs(itemLeft),
      guide: { type: 'left-edge', position: 0 },
    },
    {
      snapped: canvasWidth - itemWidth,
      dist: Math.abs(itemLeft + itemWidth - canvasWidth),
      guide: { type: 'right-edge', position: canvasWidth },
    },
    {
      snapped: canvasWidth / 2 - itemWidth / 2,
      dist: Math.abs(itemLeft + itemWidth / 2 - canvasWidth / 2),
      guide: { type: 'vertical-center', position: canvasWidth / 2 },
    },
  ];
  const yCandidates: SnapCandidate[] = [
    {
      snapped: 0,
      dist: Math.abs(itemTop),
      guide: { type: 'top-edge', position: 0 },
    },
    {
      snapped: canvasHeight - itemHeight,
      dist: Math.abs(itemTop + itemHeight - canvasHeight),
      guide: { type: 'bottom-edge', position: canvasHeight },
    },
    {
      snapped: canvasHeight / 2 - itemHeight / 2,
      dist: Math.abs(itemTop + itemHeight / 2 - canvasHeight / 2),
      guide: { type: 'horizontal-center', position: canvasHeight / 2 },
    },
  ];

  for (const t of targets) {
    const targetXs = [t.left, t.left + t.width / 2, t.left + t.width];
    const targetYs = [t.top, t.top + t.height / 2, t.top + t.height];

    for (const targetX of targetXs) {
      for (const [anchorValue, anchorOffset] of anchorsX) {
        xCandidates.push({
          snapped: targetX - anchorOffset,
          dist: Math.abs(anchorValue - targetX),
          guide: { type: 'vertical-item', position: targetX },
        });
      }
    }
    for (const targetY of targetYs) {
      for (const [anchorValue, anchorOffset] of anchorsY) {
        yCandidates.push({
          snapped: targetY - anchorOffset,
          dist: Math.abs(anchorValue - targetY),
          guide: { type: 'horizontal-item', position: targetY },
        });
      }
    }
  }

  const bestX = pickNearest(xCandidates);
  const bestY = pickNearest(yCandidates);

  const activeGuides: ActiveSmartGuide[] = [];
  if (bestX) activeGuides.push(bestX.guide);
  if (bestY) activeGuides.push(bestY.guide);

  return {
    snappedLeft: Math.round(bestX ? bestX.snapped : itemLeft),
    snappedTop: Math.round(bestY ? bestY.snapped : itemTop),
    activeGuides,
  };
}
